import React, {
  memo, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { Form } from 'react-bootstrap';

import ImagePreviewModal from '@/components/media/ImagePreviewModal.js';
import { generateJianyingImageAiCaptions } from '../../../../services/jianyingJobService.js';
import { JianyingAudioSlotEditor } from './audio/JianyingAudioSlotEditor.js';
import JianyingImageCard from './image/JianyingImageCard.js';
import { JianyingTextField } from './text/JianyingTextField.js';
import { JianyingVideoSlotEditor } from './video/JianyingVideoSlotEditor.js';
import {
  buildJianyingSlotSections,
  compareJianyingSlotsByTimeline,
  createJianyingImageValue,
  JIANYING_AI_CAPTION_TOKEN,
  resolveJianyingBindingOwners,
  sortJianyingTimelineNodes,
} from '../../../../utils/jianyingSlots.js';

const toProductionSlot = (slot, aiCaptionSlotIds) => ({
  ...slot,
  aiCaptionDefault: aiCaptionSlotIds.has(String(slot.id)),
  key: slot.id,
  description: slot.name,
  runtime: slot.slot_settings || {},
  config: {
    material_id: slot.material_id,
    segment_id: slot.segment_id,
    occurrence_id: slot.occurrence_id,
    ...(slot.slot_settings || {}),
  },
});

const buildMixedMaterialModel = ({ selectedCopyPresetId, template = {} }) => {
  const selectedPreset = (Array.isArray(template.copy_presets) ? template.copy_presets : [])
    .find((preset) => String(preset.id) === String(selectedCopyPresetId));
  const aiCaptionSlotIds = new Set(
    Object.entries(selectedPreset?.values || {})
      .filter(([, value]) => value === JIANYING_AI_CAPTION_TOKEN)
      .map(([slotId]) => String(slotId)),
  );
  const slots = (Array.isArray(template.slots) ? template.slots : [])
    .filter((slot) => slot?.id && slot.replace_enabled !== false)
    .slice()
    .sort(compareJianyingSlotsByTimeline)
    .map((slot) => toProductionSlot(slot, aiCaptionSlotIds));
  if (!slots.length) return { sections: [], warnings: [] };
  const slotById = new Map(slots.map((slot) => [String(slot.id), slot]));
  const { ownerByTargetId, warnings } = resolveJianyingBindingOwners(slots);
  const targetsByOwnerId = new Map();
  ownerByTargetId.forEach((owner, targetId) => {
    const targets = targetsByOwnerId.get(String(owner.id)) || [];
    targets.push(slotById.get(targetId));
    targetsByOwnerId.set(String(owner.id), targets);
  });
  const entries = [];
  for (const slot of slots) {
    if (ownerByTargetId.has(String(slot.id))) continue;
    if (slot.kind !== 'image') {
      entries.push({ type: 'slot', key: slot.id, slot });
      continue;
    }
    const targets = (targetsByOwnerId.get(String(slot.id)) || [])
      .slice()
      .sort(compareJianyingSlotsByTimeline);
    const textSlots = targets.filter((target) => target.kind === 'text');
    const audioSlots = targets.filter((target) => target.kind === 'audio');
    entries.push({
      type: 'image',
      key: slot.id,
      slot,
      textSlots,
      audioSlots,
      extraTextCount: Math.max(0, textSlots.length - 1),
      extraAudioCount: Math.max(0, audioSlots.length - 1),
    });
  }
  const entriesById = new Map(entries.map((entry) => [String(entry.key), entry]));
  const visibleSlotIds = new Set(entriesById.keys());
  const sections = buildJianyingSlotSections({
    ...template,
    slots: (Array.isArray(template.slots) ? template.slots : [])
      .filter((slot) => visibleSlotIds.has(String(slot.id))),
  });
  const attachEntries = (nodes) => nodes.flatMap((node) => {
    if (node.type !== 'group') {
      const entry = entriesById.get(String(node.key));
      return entry ? [entry] : [];
    }
    const children = attachEntries(node.children);
    return children.length ? [{ ...node, children }] : [];
  });
  return {
    sections: sortJianyingTimelineNodes(attachEntries(sections)),
    warnings,
  };
};

const buildMixedEntryStates = ({
  audioErrors, fieldErrors, imageErrors, sections, values, videoErrors,
}) => {
  const next = new Map();
  const visit = (entry) => {
    if (entry.type === 'group') {
      entry.children.forEach(visit);
      return;
    }
    const state = entry.type === 'image' ? {
      type: 'image',
      imageValue: values[entry.slot.id],
      imageError: imageErrors[entry.slot.id],
      textValues: entry.textSlots.map((slot) => values[slot.id]),
      textErrors: entry.textSlots.map((slot) => fieldErrors[slot.id]),
      audioValues: entry.audioSlots.map((slot) => values[slot.id]),
      audioErrors: entry.audioSlots.map((slot) => audioErrors[slot.id]),
    } : {
      type: entry.slot.kind,
      value: values[entry.slot.id],
      error: entry.slot.kind === 'text'
        ? fieldErrors[entry.slot.id]
        : entry.slot.kind === 'audio'
          ? audioErrors[entry.slot.id]
          : videoErrors[entry.slot.id],
    };
    next.set(entry.key, state);
  };
  sections.forEach(visit);
  return next;
};

const safeId = (value) => String(value).replace(/[^a-zA-Z0-9_-]/g, '-');

const getKindIcon = (kind) => ({
  audio: 'bi-music-note-beamed',
  image: 'bi-image',
  text: 'bi-fonts',
  video: 'bi-camera-video',
}[kind] || 'bi-circle');

const countEntries = (nodes = []) => nodes.reduce((count, node) => (
  count + (node.type === 'group' ? countEntries(node.children) : 1)
), 0);

const hasErrorValue = (value) => (
  Array.isArray(value) ? value.length > 0 : Boolean(value)
);

const buildErrorGroupKeys = ({
  audioErrors,
  fieldErrors,
  imageErrors,
  sections,
  videoErrors,
}) => {
  const groupKeys = new Set();
  const entryHasError = (entry) => {
    if (entry.type === 'image') {
      return hasErrorValue(imageErrors[entry.slot.id])
        || entry.textSlots.some((slot) => hasErrorValue(fieldErrors[slot.id]))
        || entry.audioSlots.some((slot) => hasErrorValue(audioErrors[slot.id]));
    }
    if (entry.slot.kind === 'text') return hasErrorValue(fieldErrors[entry.slot.id]);
    if (entry.slot.kind === 'audio') return hasErrorValue(audioErrors[entry.slot.id]);
    if (entry.slot.kind === 'video') return hasErrorValue(videoErrors[entry.slot.id]);
    return false;
  };
  const visit = (node) => {
    if (node.type !== 'group') return entryHasError(node);
    let hasErrors = false;
    node.children.forEach((child) => {
      if (visit(child)) hasErrors = true;
    });
    if (hasErrors) groupKeys.add(node.key);
    return hasErrors;
  };
  sections.forEach(visit);
  return groupKeys;
};

const MixedImageEntry = memo(({
  disabled,
  entry,
  image,
  index,
  isFull,
  onAudioChange,
  onCopy,
  onError,
  onImageChange,
  onPreview,
  onTextChange,
  registerFieldRef,
  registerImageFieldRef,
  state,
  suppressDropAnimation,
}) => {
  const [textExpanded, setTextExpanded] = useState(false);
  const [audioExpanded, setAudioExpanded] = useState(false);
  const textContentId = `jianying-bound-text-${safeId(entry.slot.id)}`;
  const audioContentId = `jianying-bound-audio-${safeId(entry.slot.id)}`;
  const primaryTextSlots = entry.textSlots.slice(0, 1);
  const primaryAudioSlots = entry.audioSlots.slice(0, 1);
  const extraTextSlots = entry.textSlots.slice(1);
  const extraAudioSlots = entry.audioSlots.slice(1);
  const extraTextHasErrors = state.textErrors.slice(1).some(hasErrorValue);
  const extraAudioHasErrors = state.audioErrors.slice(1).some(hasErrorValue);
  const effectiveTextExpanded = textExpanded || extraTextHasErrors;
  const effectiveAudioExpanded = audioExpanded || extraAudioHasErrors;
  const hasComparison = Boolean(image?.crop_image_url || image?.fix_image_url);

  useEffect(() => {
    if (extraTextHasErrors) setTextExpanded(true);
  }, [extraTextHasErrors]);
  useEffect(() => {
    if (extraAudioHasErrors) setAudioExpanded(true);
  }, [extraAudioHasErrors]);

  const embeddedMaterials = entry.textSlots.length || entry.audioSlots.length ? (
    <div className="jianying-mixed-image-materials">
      {entry.textSlots.length ? (
        <div className="jianying-mixed-image-materials__kind">
          {primaryTextSlots.map((slot, index) => (
            <JianyingTextField
              key={slot.id}
              disabled={disabled}
              errors={state.textErrors[index]}
              onChange={onTextChange}
              registerFieldRef={registerFieldRef}
              slot={slot}
              value={state.textValues[index]}
            />
          ))}
          <div id={textContentId} hidden={!effectiveTextExpanded}>
            {extraTextSlots.map((slot, index) => (
              <JianyingTextField
                key={slot.id}
                disabled={disabled}
                errors={state.textErrors[index + 1]}
                onChange={onTextChange}
                registerFieldRef={registerFieldRef}
                slot={slot}
                value={state.textValues[index + 1]}
              />
            ))}
          </div>
          {entry.extraTextCount ? (
            <button
              type="button"
              className="jianying-mixed-image-materials__toggle"
              aria-expanded={effectiveTextExpanded}
              aria-controls={textContentId}
              onClick={() => setTextExpanded((current) => !current)}
            >
              <i className={`bi bi-chevron-${effectiveTextExpanded ? 'up' : 'down'}`} aria-hidden="true" />
              {effectiveTextExpanded ? '收起其余文字' : `展开另外 ${entry.extraTextCount} 个文字`}
            </button>
          ) : null}
        </div>
      ) : null}
      {entry.audioSlots.length ? (
        <div className="jianying-mixed-image-materials__kind">
          {primaryAudioSlots.map((slot, index) => (
            <JianyingAudioSlotEditor
              key={slot.id}
              disabled={disabled}
              error={state.audioErrors[index]}
              onChange={(value) => onAudioChange(slot.id, value)}
              onError={onError}
              registerFieldRef={registerFieldRef}
              slot={slot}
              value={state.audioValues[index]}
            />
          ))}
          <div id={audioContentId} hidden={!effectiveAudioExpanded}>
            {extraAudioSlots.map((slot, index) => (
              <JianyingAudioSlotEditor
                key={slot.id}
                disabled={disabled}
                error={state.audioErrors[index + 1]}
                onChange={(value) => onAudioChange(slot.id, value)}
                onError={onError}
                registerFieldRef={registerFieldRef}
                slot={slot}
                value={state.audioValues[index + 1]}
              />
            ))}
          </div>
          {entry.extraAudioCount ? (
            <button
              type="button"
              className="jianying-mixed-image-materials__toggle"
              aria-expanded={effectiveAudioExpanded}
              aria-controls={audioContentId}
              onClick={() => setAudioExpanded((current) => !current)}
            >
              <i className={`bi bi-chevron-${effectiveAudioExpanded ? 'up' : 'down'}`} aria-hidden="true" />
              {effectiveAudioExpanded ? '收起其余音频' : `展开另外 ${entry.extraAudioCount} 个音频`}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      className={`jianying-mixed-entry is-image ${hasComparison ? 'has-comparison' : ''} ${state.imageError ? 'is-invalid' : ''}`}
      ref={(node) => registerImageFieldRef?.(entry.slot.id, node)}
      tabIndex={-1}
    >
      <span className="jianying-mixed-entry__node" aria-hidden="true">
        <i className="bi bi-image" />
      </span>
      {image ? <JianyingImageCard
        assignmentLabel={entry.slot.name}
        disabled={disabled}
        entry={{
          id: entry.slot.id,
          state: image ? 'selected' : 'unavailable',
          item: state.imageValue,
          image,
        }}
        isSelected
        copyDisabled={isFull}
        index={index}
        onCopy={() => onCopy(state.imageValue)}
        onPreview={onPreview}
        onRemove={() => onImageChange(entry.slot.id, null)}
        onSourceChange={(source) => onImageChange(entry.slot.id, {
          ...state.imageValue,
          source,
        })}
        showSequenceControls
        sortable={Boolean(image)}
        suppressDropAnimation={suppressDropAnimation}
      /> : <div className="jianying-empty">未选择图片</div>}
      {embeddedMaterials}
      {state.imageError ? (
        <div className="jianying-mixed-entry__error" role="alert">
          {state.imageError}
        </div>
      ) : null}
    </div>
  );
});

const MixedImageAssignmentGrid = ({
  clientId,
  disabled,
  entries,
  entryStates,
  imagesById,
  onAudioChange,
  onError,
  onImageChange,
  onPreview,
  onTextChange,
  orderImages,
  registerFieldRef,
  registerImageFieldRef,
}) => {
  const [suppressDropAnimation, setSuppressDropAnimation] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [captionState, setCaptionState] = useState({ busy: false, message: '' });
  const captionGeneration = useRef(0);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  }));
  const selectedAssignments = entries.flatMap((entry) => {
    const value = entryStates.get(entry.key)?.imageValue;
    return value?.image_id ? [{ entry, value }] : [];
  });
  const captionAssignments = selectedAssignments.filter(
    ({ entry }) => entry.textSlots.some((slot) => slot.aiCaptionDefault),
  );
  const activeAssignment = selectedAssignments.find(
    ({ entry }) => String(entry.slot.id) === String(activeId),
  );
  const activeImage = activeAssignment
    ? imagesById.get(String(activeAssignment.value.image_id))
    : null;
  const activeImageUrl = activeAssignment?.value.source === 'original'
    ? activeImage?.image_url
    : activeImage?.crop_image_url || activeImage?.fix_image_url || activeImage?.image_url;
  const selectedImageIds = new Set(
    selectedAssignments.map(({ value }) => String(value.image_id)),
  );
  const availableImages = orderImages.filter(
    (image) => !selectedImageIds.has(String(image.id ?? image.image_id)),
  );
  const isFull = selectedAssignments.length >= entries.length;
  const selectImage = (image, source) => {
    const emptyEntry = entries.find(
      (entry) => !entryStates.get(entry.key)?.imageValue?.image_id,
    );
    if (emptyEntry) {
      onImageChange(emptyEntry.slot.id, createJianyingImageValue(image, source));
    }
  };
  const copyImage = (value) => {
    const emptyEntry = entries.find(
      (entry) => !entryStates.get(entry.key)?.imageValue?.image_id,
    );
    if (emptyEntry) onImageChange(emptyEntry.slot.id, value);
  };
  const handleDragEnd = ({ active, over }) => {
    setSuppressDropAnimation(true);
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = selectedAssignments.findIndex(({ entry }) => entry.slot.id === active.id);
    const newIndex = selectedAssignments.findIndex(({ entry }) => entry.slot.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(selectedAssignments, oldIndex, newIndex);
    selectedAssignments.forEach(({ entry }, index) => {
      onImageChange(entry.slot.id, reordered[index].value);
    });
  };

  const generateCaptions = async () => {
    if (captionState.busy || !captionAssignments.length) return;
    const generation = captionGeneration.current;
    setCaptionState({ busy: true, message: '' });
    try {
      const data = await generateJianyingImageAiCaptions({
        clientId,
        images: captionAssignments.map(({ entry, value }) => ({
          image_slot_id: entry.slot.id,
          image_id: value.image_id,
          source: value.source === 'original' ? 'original' : 'processed',
          text_slots: entry.textSlots
            .filter((slot) => slot.aiCaptionDefault)
            .map((slot) => ({
              id: slot.id,
              duration_us: Number(slot.runtime?.timeline_duration_us
                ?? slot.runtime?.target_duration_us),
            })),
        })),
      });
      if (generation !== captionGeneration.current) return;
      (Array.isArray(data?.items) ? data.items : []).forEach((item) => {
        onTextChange(item.text_slot_id, item.ai_caption, { manual: false });
      });
      setCaptionState({
        busy: false,
        message: `已生成 ${captionAssignments.length} 张图片的文案。`,
      });
    } catch (error) {
      if (generation === captionGeneration.current) {
        const message = typeof error?.response?.data?.message === 'string'
          ? error.response.data.message.trim()
          : '';
        setCaptionState({
          busy: false,
          message: message || '文案生成失败，请稍后重试。',
        });
      }
    }
  };

  useEffect(() => {
    if (!suppressDropAnimation) return undefined;
    const frameId = window.requestAnimationFrame(() => setSuppressDropAnimation(false));
    return () => window.cancelAnimationFrame(frameId);
  }, [suppressDropAnimation]);

  useEffect(() => {
    captionGeneration.current += 1;
    setCaptionState({ busy: false, message: '' });
  }, [clientId]);

  return (
    <div className="jianying-image-assignment jianying-mixed-image-assignment">
      <div className="jianying-image-assignment__summary">
        <span>已选 {selectedAssignments.length} / {entries.length} · 按点击顺序分配，可拖动已选图片调整顺序</span>
        {captionAssignments.length ? (
          <button
            type="button"
                disabled={disabled || captionState.busy || selectedAssignments.length !== entries.length}
            onClick={generateCaptions}
          >
            {captionState.busy ? '生成中…' : '生成文案'}
          </button>
        ) : null}
      </div>
      {captionState.message ? (
        <p className="jianying-notice" role="status">{captionState.message}</p>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragCancel={() => {
          setActiveId(null);
          setSuppressDropAnimation(true);
        }}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={selectedAssignments.map(({ entry }) => entry.slot.id)}
          strategy={rectSortingStrategy}
        >
          <div className="jianying-group-image-grid">
            {selectedAssignments.map(({ entry, value }, index) => (
              <MixedImageEntry
                key={entry.key}
                disabled={disabled}
                entry={entry}
                image={imagesById.get(String(value.image_id)) || null}
                index={index}
                isFull={isFull}
                onAudioChange={onAudioChange}
                onCopy={copyImage}
                onError={onError}
                onImageChange={onImageChange}
                onPreview={onPreview}
                onTextChange={onTextChange}
                registerFieldRef={registerFieldRef}
                registerImageFieldRef={registerImageFieldRef}
                state={entryStates.get(entry.key)}
                suppressDropAnimation={suppressDropAnimation}
              />
            ))}
            {availableImages.map((image, index) => (
              <JianyingImageCard
                key={image.id ?? image.image_id}
                disabled={disabled || isFull}
                entry={{
                  id: `available-${image.id ?? image.image_id}`,
                  state: isFull ? 'unavailable' : 'unselected',
                  item: null,
                  image,
                }}
                index={index}
                onPreview={onPreview}
                onSelect={selectImage}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeAssignment && activeImageUrl ? (
            <div className="jianying-image-drag-overlay">
              <img src={activeImageUrl} alt="" />
              <strong>{activeAssignment.entry.slot.name}</strong>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {!orderImages.length ? <div className="jianying-empty">该订单没有可用图片</div> : null}
    </div>
  );
};

const StandaloneEntry = memo(({
  disabled,
  entry,
  onAudioChange,
  onError,
  onImageChange = () => {},
  onTextChange,
  onVideoChange,
  registerFieldRef,
  state,
}) => {
  const { slot } = entry;
  let editor = null;
  if (slot.kind === 'text') {
    editor = (
      <JianyingTextField
        disabled={disabled}
        errors={state.error}
        onChange={onTextChange}
        registerFieldRef={registerFieldRef}
        slot={slot}
        value={state.value}
      />
    );
  } else if (slot.kind === 'audio') {
    editor = (
      <JianyingAudioSlotEditor
        disabled={disabled}
        error={state.error}
        onChange={(value) => onAudioChange(slot.id, value)}
        onError={onError}
        registerFieldRef={registerFieldRef}
        slot={slot}
        value={state.value}
      />
    );
  } else if (slot.kind === 'video') {
    editor = (
      <JianyingVideoSlotEditor
        disabled={disabled}
        error={state.error}
        onChange={(value) => onVideoChange(slot.id, value)}
        onError={onError}
        registerFieldRef={registerFieldRef}
        slot={slot}
        value={state.value}
      />
    );
  }
  if (!editor) return null;

  return (
    <div className={`jianying-mixed-entry is-${slot.kind}`}>
      <span className="jianying-mixed-entry__node" aria-hidden="true">
        <i className={`bi ${getKindIcon(slot.kind)}`} />
      </span>
      <div className="jianying-mixed-entry__standalone">{editor}</div>
    </div>
  );
});

const MixedGroup = ({
  depth = 0,
  errorGroupKeys,
  node,
  renderNodes,
}) => {
  const [expanded, setExpanded] = useState(true);
  const contentId = `jianying-mixed-group-${safeId(node.key)}`;
  const entryCount = countEntries(node.children);
  const hasErrors = errorGroupKeys.has(node.key);
  const effectiveExpanded = expanded || hasErrors;

  useEffect(() => {
    if (hasErrors) setExpanded(true);
  }, [hasErrors]);

  return (
    <section
      className="jianying-mixed-group"
      style={{ '--jy-mixed-depth': depth }}
    >
      <button
        type="button"
        className="jianying-mixed-group__header"
        aria-expanded={effectiveExpanded}
        aria-controls={contentId}
        onClick={() => setExpanded((current) => !current)}
      >
        <i className={`bi bi-chevron-${effectiveExpanded ? 'down' : 'right'}`} aria-hidden="true" />
        <span>
          <strong>{node.group.name}</strong>
          <small>{entryCount} 项素材</small>
        </span>
      </button>
      <div
        className="jianying-mixed-group__content"
        id={contentId}
        hidden={!effectiveExpanded}
      >
        {renderNodes(node.children, depth + 1)}
      </div>
    </section>
  );
};

const JianyingMixedMaterialPanel = ({
  audioErrors = {},
  clientId,
  copyPresets = [],
  disabled = false,
  fieldErrors = {},
  imageErrors = {},
  onAudioChange,
  onError,
  onImageChange,
  onPresetChange,
  onTextChange,
  onVideoChange,
  orderImages = [],
  registerFieldRef,
  registerImageFieldRef,
  selectedCopyPresetId = '',
  template,
  values = {},
  videoErrors = {},
}) => {
  const [preview, setPreview] = useState(null);
  const model = useMemo(
    () => buildMixedMaterialModel({ selectedCopyPresetId, template }),
    [selectedCopyPresetId, template],
  );
  const imagesById = useMemo(() => new Map(
    orderImages.map((image) => [String(image.id ?? image.image_id), image]),
  ), [orderImages]);
  const entryStates = useMemo(() => buildMixedEntryStates({
    audioErrors,
    fieldErrors,
    imageErrors,
    sections: model.sections,
    values,
    videoErrors,
  }), [audioErrors, fieldErrors, imageErrors, model.sections, values, videoErrors]);
  const materialCount = useMemo(
    () => countEntries(model.sections),
    [model.sections],
  );
  const errorGroupKeys = useMemo(() => buildErrorGroupKeys({
    audioErrors,
    fieldErrors,
    imageErrors,
    sections: model.sections,
    videoErrors,
  }), [audioErrors, fieldErrors, imageErrors, model.sections, videoErrors]);
  const renderStandaloneEntry = (entry) => (
    <StandaloneEntry
      key={entry.key}
      disabled={disabled}
      entry={entry}
      onAudioChange={onAudioChange}
      onError={onError}
      onTextChange={onTextChange}
      onVideoChange={onVideoChange}
      registerFieldRef={registerFieldRef}
      state={entryStates.get(entry.key)}
    />
  );
  const renderNodes = (nodes, depth = 0) => {
    const rendered = [];
    let imageEntries = [];
    const flushImages = () => {
      if (!imageEntries.length) return;
      const entries = imageEntries;
      imageEntries = [];
      rendered.push(
        <MixedImageAssignmentGrid
          key={`images-${entries.map((entry) => entry.key).join('-')}`}
          clientId={clientId}
          disabled={disabled}
          entries={entries}
          entryStates={entryStates}
          imagesById={imagesById}
          onAudioChange={onAudioChange}
          onError={onError}
          onImageChange={onImageChange}
          onPreview={setPreview}
          onTextChange={onTextChange}
          orderImages={orderImages}
          registerFieldRef={registerFieldRef}
          registerImageFieldRef={registerImageFieldRef}
        />,
      );
    };
    nodes.forEach((node) => {
      if (node.type === 'image') {
        imageEntries.push(node);
        return;
      }
      flushImages();
      rendered.push(node.type === 'group' ? (
        <MixedGroup
          key={node.key}
          depth={depth}
          errorGroupKeys={errorGroupKeys}
          node={node}
          renderNodes={renderNodes}
        />
      ) : renderStandaloneEntry(node));
    });
    flushImages();
    return rendered;
  };

  return (
    <>
    <section className="jianying-material-panel jianying-mixed-material-panel">
      <div className="jianying-material-panel__header jianying-mixed-material-panel__header">
        <span>
          <i className="bi bi-layers" aria-hidden="true" />
          <strong>素材时间线</strong>
          <small>{materialCount} 项</small>
        </span>
        {copyPresets.length ? (
          <Form.Group className="jianying-copy-preset-switch" controlId="jianying-copy-preset">
            <Form.Label>文案模板</Form.Label>
            <Form.Select
              disabled={disabled}
              value={selectedCopyPresetId}
              onChange={(event) => onPresetChange(event.target.value)}
            >
              {copyPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.name}{preset.is_default ? '（默认）' : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        ) : null}
      </div>

      {model.warnings.length ? (
        <div className="jianying-mixed-material-panel__warning" role="status" aria-live="polite">
          模板中有 {model.warnings.length} 处槽位绑定异常，已保留每个素材的首个有效归属。
        </div>
      ) : null}

      {model.sections.length ? (
        <div className="jianying-mixed-material-panel__timeline">
          {renderNodes(model.sections)}
        </div>
      ) : (
        <div className="jianying-empty">该模板没有需要替换的素材，可直接创建任务。</div>
      )}
    </section>
    <ImagePreviewModal
      alt={preview?.alt || ''}
      onClose={() => setPreview(null)}
      show={Boolean(preview?.src)}
      src={preview?.src || ''}
      title={preview?.title || '图片预览'}
    />
    </>
  );
};

export default JianyingMixedMaterialPanel;
